import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HelmetProvider } from 'react-helmet-async';

// Loading Spinner Component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#E06D53] border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-600 dark:text-slate-400 font-medium">Loading...</p>
    </div>
  </div>
);

// Critical pages - load immediately (auth flow)
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import SignupPage from "@/pages/SignupPage";
import AuthCallback from "@/pages/AuthCallback";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";

// Blog pages - Public, load immediately for SEO
const BlogList = lazy(() => import("@/pages/blog/BlogList"));
const BlogPost = lazy(() => import("@/pages/blog/BlogPost"));

// Lazy loaded pages - only load when needed
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/EnterpriseDashboard"));

// Work Reporter - Lazy
const SubmitReport = lazy(() => import("@/pages/WorkReporter/SubmitReport"));
const AllReports = lazy(() => import("@/pages/WorkReporter/EnterpriseAllReports"));
const ReportAnalytics = lazy(() => import("@/pages/WorkReporter/ReportAnalytics"));

// Complaints - Lazy
const ComplaintsDashboard = lazy(() => import("@/pages/Complaints/EnterpriseComplaintsDashboard"));
const PublicComplaintForm = lazy(() => import("@/pages/Complaints/PublicComplaintForm"));

// Sales - Lazy
const SalesPipeline = lazy(() => import("@/pages/Sales/EnterpriseSalesPipeline"));
const EnterpriseSalesTracker = lazy(() => import("@/pages/sales/EnterpriseSalesTracker"));
const DealDetailPage = lazy(() => import("@/pages/sales/DealDetailPage"));
const ContactDetailPage = lazy(() => import("@/pages/sales/ContactDetailPage"));
const AISalesCoachPage = lazy(() => import("@/pages/sales/AISalesCoachPage"));
const SalesLeaderboard = lazy(() => import("@/pages/Sales/SalesLeaderboard"));
const SalesAnalytics = lazy(() => import("@/pages/Sales/SalesAnalytics"));

// Settings & Admin - Lazy
const Settings = lazy(() => import("@/pages/Settings"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const AdvancedAnalytics = lazy(() => import("@/pages/AdvancedAnalytics"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const NotificationPreferences = lazy(() => import("@/pages/NotificationPreferences"));
const TeamManagement = lazy(() => import("@/pages/TeamManagement"));
const ActivityFeed = lazy(() => import("@/pages/ActivityFeed"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));

// Attendance Module - Lazy
const AttendanceModule = lazy(() => import("@/pages/attendance/AttendanceModule"));
const AttendanceCheckIn = lazy(() => import("@/pages/attendance/EnterpriseAttendanceDashboard"));
const TodayAttendance = lazy(() => import("@/pages/attendance/TodayAttendance"));
const AttendanceHistory = lazy(() => import("@/pages/attendance/AttendanceHistory"));
const AttendanceAnalytics = lazy(() => import("@/pages/attendance/AttendanceAnalytics"));
const LeaveManagement = lazy(() => import("@/pages/attendance/LeaveManagement"));
const AttendanceSettings = lazy(() => import("@/pages/attendance/AttendanceSettings"));
const ShiftManagement = lazy(() => import("@/pages/attendance/ShiftManagement"));
const RegularizationPage = lazy(() => import("@/pages/attendance/RegularizationPage"));
const CompOffPage = lazy(() => import("@/pages/attendance/CompOffPage"));
const LiveMapPage = lazy(() => import("@/pages/attendance/LiveMapPage"));
const LeaderboardPage = lazy(() => import("@/pages/attendance/LeaderboardPage"));
const FieldVisitPage = lazy(() => import("@/pages/attendance/FieldVisitPage"));

// Attendance Enterprise Module - Lazy loaded with dynamic import
const AttEntDashboard = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.AttendanceDashboard })));
const AttEntMyAttendance = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.MyAttendance })));
const AttEntLeaveManagement = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.LeaveManagementPage })));
const AttEntLeaveCalendar = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.LeaveCalendar })));
const AttEntShiftManagement = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.ShiftManagementPage })));
const AttEntFieldAttendance = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.FieldAttendancePage })));
const AttEntRegularization = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.RegularizationPage })));
const AttEntTeamAttendance = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.TeamAttendancePage })));
const AttEntReportsAnalytics = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.ReportsAnalyticsPage })));
const AttEntPayrollIntegration = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.PayrollIntegrationPage })));
const AttEntLeaderboard = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.LeaderboardPage })));
const AttEntBadges = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.BadgesAchievementsPage })));
const AttEntAIInsights = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.AIInsightsPage })));
const AttEntSettings = lazy(() => import("@/pages/attendance-enterprise").then(m => ({ default: m.AttendanceSettingsPage })));

// Tasks Module - Lazy
const TaskManager = lazy(() => import("@/pages/tasks/TaskManager"));
const Projects = lazy(() => import("@/pages/tasks/Projects"));
const ProjectDetail = lazy(() => import("@/pages/tasks/ProjectDetail"));
const Sprints = lazy(() => import("@/pages/tasks/Sprints"));
const TaskAnalytics = lazy(() => import("@/pages/tasks/TaskAnalytics.jsx"));
const TimeLogs = lazy(() => import("@/pages/tasks/TimeLogs"));
const AIAssistant = lazy(() => import("@/pages/tasks/AIAssistant"));

// Documents Module - Lazy
const AllDocuments = lazy(() => import("@/pages/documents/EnterpriseDocuments"));
const MyDocuments = lazy(() => import("@/pages/documents/MyDocuments"));
const SharedWithMe = lazy(() => import("@/pages/documents/SharedWithMe"));
const Templates = lazy(() => import("@/pages/documents/Templates"));
const DocTrash = lazy(() => import("@/pages/documents/Trash"));
const DocumentSettings = lazy(() => import("@/pages/documents/DocumentSettings"));

// AI Insights Module - Lazy
const AIOverview = lazy(() => import("@/pages/ai-insights/AIOverview"));
const TeamPerformance = lazy(() => import("@/pages/ai-insights/TeamPerformance"));
const SalesIntelligence = lazy(() => import("@/pages/ai-insights/SalesIntelligence"));
const ComplaintIntelligence = lazy(() => import("@/pages/ai-insights/ComplaintIntelligence"));
const AttendanceIntelligence = lazy(() => import("@/pages/ai-insights/AttendanceIntelligence"));
const TaskIntelligence = lazy(() => import("@/pages/ai-insights/TaskIntelligence"));
const WeeklyReport = lazy(() => import("@/pages/ai-insights/WeeklyReport"));
const AIChat = lazy(() => import("@/pages/ai-insights/AIChat"));
const AISettings = lazy(() => import("@/pages/ai-insights/AISettings"));

// Notice Board Module - Lazy
const NoticeBoard = lazy(() => import("@/pages/noticeboard/EnterpriseNoticeBoard"));
const CreateAnnouncement = lazy(() => import("@/pages/noticeboard/CreateAnnouncement"));

// Layout
import DashboardLayout from "@/components/layout/DashboardLayout";

// Onboarding Walkthrough - keep inline for global access
import { OnboardingWalkthrough as OnboardingModal } from "@/components/OnboardingWalkthrough";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    if (user || location.state?.user) {
      setIsVerifying(false);
      return;
    }
    
    if (loading) {
      return;
    }
    
    const verify = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate("/auth", { replace: true });
      }
      setIsVerifying(false);
    };
    
    verify();
  }, [loading, user, navigate, checkAuth, location.state]);

  if (loading || isVerifying) {
    return <PageLoader />;
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// Lazy Route wrapper - combines Suspense with route
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const hasShownOnboarding = useRef(false);

  useEffect(() => {
    if (
      user && 
      !hasShownOnboarding.current && 
      location.pathname.startsWith('/dashboard')
    ) {
      const hasSeenOnboarding = localStorage.getItem(`onboarding_seen_${user.user_id}`);
      if (!hasSeenOnboarding) {
        setShowOnboarding(true);
        hasShownOnboarding.current = true;
      }
    }
  }, [user, location.pathname]);

  const handleOnboardingComplete = () => {
    if (user) {
      localStorage.setItem(`onboarding_seen_${user.user_id}`, 'true');
    }
    setShowOnboarding(false);
  };

  // Redirect root to landing if not authenticated, dashboard if authenticated
  useEffect(() => {
    if (location.pathname === "/" && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [location.pathname, user, navigate]);

  return (
    <>
      <Routes>
        {/* Public routes - loaded immediately */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/submit-complaint/:companyId" element={<LazyRoute><PublicComplaintForm /></LazyRoute>} />
        
        {/* Blog routes - Public, no auth required */}
        <Route path="/blog" element={<LazyRoute><BlogList /></LazyRoute>} />
        <Route path="/blog/:slug" element={<LazyRoute><BlogPost /></LazyRoute>} />
        
        {/* Protected routes with lazy loading */}
        <Route path="/onboarding" element={<ProtectedRoute><LazyRoute><Onboarding /></LazyRoute></ProtectedRoute>} />
        
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<LazyRoute><Dashboard /></LazyRoute>} />
          
          {/* Work Reporter */}
          <Route path="work-reporter/submit" element={<LazyRoute><SubmitReport /></LazyRoute>} />
          <Route path="reports" element={<LazyRoute><AllReports /></LazyRoute>} />
          <Route path="reports/analytics" element={<LazyRoute><ReportAnalytics /></LazyRoute>} />
          
          {/* Complaints */}
          <Route path="complaints" element={<LazyRoute><ComplaintsDashboard /></LazyRoute>} />
          
          {/* Sales */}
          <Route path="sales" element={<LazyRoute><EnterpriseSalesTracker /></LazyRoute>} />
          <Route path="sales/pipeline" element={<LazyRoute><SalesPipeline /></LazyRoute>} />
          <Route path="sales/deal/:dealId" element={<LazyRoute><DealDetailPage /></LazyRoute>} />
          <Route path="sales/contact/:contactId" element={<LazyRoute><ContactDetailPage /></LazyRoute>} />
          <Route path="sales/ai-coach" element={<LazyRoute><AISalesCoachPage /></LazyRoute>} />
          <Route path="sales/leaderboard" element={<LazyRoute><SalesLeaderboard /></LazyRoute>} />
          <Route path="sales/analytics" element={<LazyRoute><SalesAnalytics /></LazyRoute>} />
          
          {/* Attendance - New Design */}
          <Route path="attendance" element={<LazyRoute><AttendanceModule /></LazyRoute>} />
          
          {/* Attendance (legacy) */}
          <Route path="attendance/checkin" element={<LazyRoute><AttendanceCheckIn /></LazyRoute>} />
          <Route path="attendance/today" element={<LazyRoute><TodayAttendance /></LazyRoute>} />
          <Route path="attendance/history" element={<LazyRoute><AttendanceHistory /></LazyRoute>} />
          <Route path="attendance/analytics" element={<LazyRoute><AttendanceAnalytics /></LazyRoute>} />
          <Route path="attendance/leaves" element={<LazyRoute><LeaveManagement /></LazyRoute>} />
          <Route path="attendance/shifts" element={<LazyRoute><ShiftManagement /></LazyRoute>} />
          <Route path="attendance/regularization" element={<LazyRoute><RegularizationPage /></LazyRoute>} />
          <Route path="attendance/comp-off" element={<LazyRoute><CompOffPage /></LazyRoute>} />
          <Route path="attendance/live-map" element={<LazyRoute><LiveMapPage /></LazyRoute>} />
          <Route path="attendance/leaderboard" element={<LazyRoute><LeaderboardPage /></LazyRoute>} />
          <Route path="attendance/field-visit" element={<LazyRoute><FieldVisitPage /></LazyRoute>} />
          <Route path="attendance/settings" element={<LazyRoute><AttendanceSettings /></LazyRoute>} />
          
          {/* Attendance Enterprise */}
          <Route path="att/dashboard" element={<LazyRoute><AttEntDashboard /></LazyRoute>} />
          <Route path="att/my-attendance" element={<LazyRoute><AttEntMyAttendance /></LazyRoute>} />
          <Route path="att/leaves" element={<LazyRoute><AttEntLeaveManagement /></LazyRoute>} />
          <Route path="att/leave-calendar" element={<LazyRoute><AttEntLeaveCalendar /></LazyRoute>} />
          <Route path="att/shifts" element={<LazyRoute><AttEntShiftManagement /></LazyRoute>} />
          <Route path="att/field" element={<LazyRoute><AttEntFieldAttendance /></LazyRoute>} />
          <Route path="att/regularization" element={<LazyRoute><AttEntRegularization /></LazyRoute>} />
          <Route path="att/team" element={<LazyRoute><AttEntTeamAttendance /></LazyRoute>} />
          <Route path="att/reports" element={<LazyRoute><AttEntReportsAnalytics /></LazyRoute>} />
          <Route path="att/payroll" element={<LazyRoute><AttEntPayrollIntegration /></LazyRoute>} />
          <Route path="att/leaderboard" element={<LazyRoute><AttEntLeaderboard /></LazyRoute>} />
          <Route path="att/badges" element={<LazyRoute><AttEntBadges /></LazyRoute>} />
          <Route path="att/ai-insights" element={<LazyRoute><AttEntAIInsights /></LazyRoute>} />
          <Route path="att/settings" element={<LazyRoute><AttEntSettings /></LazyRoute>} />
          
          {/* Tasks */}
          <Route path="tasks" element={<LazyRoute><TaskManager /></LazyRoute>} />
          <Route path="tasks/projects" element={<LazyRoute><Projects /></LazyRoute>} />
          <Route path="tasks/projects/:projectId" element={<LazyRoute><ProjectDetail /></LazyRoute>} />
          <Route path="tasks/sprints" element={<LazyRoute><Sprints /></LazyRoute>} />
          <Route path="tasks/analytics" element={<LazyRoute><TaskAnalytics /></LazyRoute>} />
          <Route path="tasks/time-logs" element={<LazyRoute><TimeLogs /></LazyRoute>} />
          <Route path="tasks/ai-assistant" element={<LazyRoute><AIAssistant /></LazyRoute>} />
          
          {/* Documents */}
          <Route path="documents" element={<LazyRoute><AllDocuments /></LazyRoute>} />
          <Route path="documents/my" element={<LazyRoute><MyDocuments /></LazyRoute>} />
          <Route path="documents/shared" element={<LazyRoute><SharedWithMe /></LazyRoute>} />
          <Route path="documents/templates" element={<LazyRoute><Templates /></LazyRoute>} />
          <Route path="documents/trash" element={<LazyRoute><DocTrash /></LazyRoute>} />
          <Route path="documents/settings" element={<LazyRoute><DocumentSettings /></LazyRoute>} />
          
          {/* AI Insights */}
          <Route path="ai-insights" element={<LazyRoute><AIOverview /></LazyRoute>} />
          <Route path="ai-insights/team" element={<LazyRoute><TeamPerformance /></LazyRoute>} />
          <Route path="ai-insights/sales" element={<LazyRoute><SalesIntelligence /></LazyRoute>} />
          <Route path="ai-insights/complaints" element={<LazyRoute><ComplaintIntelligence /></LazyRoute>} />
          <Route path="ai-insights/attendance" element={<LazyRoute><AttendanceIntelligence /></LazyRoute>} />
          <Route path="ai-insights/tasks" element={<LazyRoute><TaskIntelligence /></LazyRoute>} />
          <Route path="ai-insights/weekly" element={<LazyRoute><WeeklyReport /></LazyRoute>} />
          <Route path="ai-insights/chat" element={<LazyRoute><AIChat /></LazyRoute>} />
          <Route path="ai-insights/settings" element={<LazyRoute><AISettings /></LazyRoute>} />
          
          {/* Notice Board */}
          <Route path="noticeboard" element={<LazyRoute><NoticeBoard /></LazyRoute>} />
          <Route path="noticeboard/create" element={<LazyRoute><CreateAnnouncement /></LazyRoute>} />
          
          {/* Settings & Admin */}
          <Route path="settings" element={<LazyRoute><Settings /></LazyRoute>} />
          <Route path="team" element={<LazyRoute><TeamManagement /></LazyRoute>} />
          <Route path="billing" element={<LazyRoute><BillingPage /></LazyRoute>} />
          <Route path="billing/success" element={<LazyRoute><PaymentSuccessPage /></LazyRoute>} />
          <Route path="activity" element={<LazyRoute><ActivityFeed /></LazyRoute>} />
          <Route path="analytics" element={<LazyRoute><AdvancedAnalytics /></LazyRoute>} />
          <Route path="audit-logs" element={<LazyRoute><AuditLogs /></LazyRoute>} />
          <Route path="security" element={<LazyRoute><SecuritySettings /></LazyRoute>} />
          <Route path="notifications" element={<LazyRoute><NotificationPreferences /></LazyRoute>} />
          <Route path="status" element={<LazyRoute><StatusPage /></LazyRoute>} />
        </Route>
      </Routes>
      
      {showOnboarding && (
        <OnboardingModal 
          isOpen={showOnboarding} 
          onComplete={handleOnboardingComplete}
          onClose={handleOnboardingComplete}
        />
      )}
      
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
